import React, { useState } from "react";
import { Grid, TextField, styled, TextFieldProps, IconButton } from "@mui/material";
import CustomTooltip from "src/components/uiElements/form/customTooltip";
import {
  Control,
  Controller,
  ControllerProps,
  useFormContext,
} from "react-hook-form";
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const CustomTimePickerField = styled(TextField)`
  & input[type="time"] {
    padding-top: ${(props) =>
      props.theme.spacing(1)}; /* Adjust padding for a smaller size */
    padding-bottom: ${(props) =>
      props.theme.spacing(1)}; /* Adjust padding for a smaller size */
    font-size: 18px; /* Adjust font size for a smaller size */
  }
`;

const IconWrapper = styled("div")`
  display: flex;
  align-items: center;
  padding-right: ${(props) => props.theme.spacing(0.1)};
`;

interface CustomTimePickerFieldProps {
  validation?: ControllerProps["rules"];
  label?: string;
  name: string;
  toolTipTitle?: string;
  control?: Control<any>;
  inputProps?: TextFieldProps;
  onChange?: (time: string) => void; // Expose onChange prop
}

const CustomTimePicker: React.FC<CustomTimePickerFieldProps> = ({
  validation = {},
  label,
  name,
  toolTipTitle,
  control,
  inputProps,
  onChange, // Receive the onChange prop from the parent
  ...rest
}) => {
  const { register } = useFormContext();
  const [showTimePicker, setShowTimePicker] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Grid container alignItems="center">
          <Grid item>
            <Grid container alignItems="center">
              <Grid item>
                <Grid container alignItems="center">
                  <Grid
                    item
                    sx={{
                      fontSize: "14px",
                      fontWeight: "300",
                      color: "#797979",
                      mb: "4px",
                      ml: "4px",
                      mr: "6px",
                    }}
                  >
                    {label}
                  </Grid>
                  {toolTipTitle && <CustomTooltip title={toolTipTitle} />}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <CustomTimePickerField
            fullWidth
            type="time"
            {...rest}
            {...register(name)}
            value={value || ""}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 60, // This ensures AM/PM is shown in Chrome
            }}
            onChange={(event) => {
              let item = event.target.value;
              onChange(item);
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => {
                    const input = document.querySelector(`input[name="${name}"]`) as HTMLInputElement;
                    if (input) {
                      input.showPicker();
                    }
                  }}
                  sx={{
                    color: "#ffffff",
                    "&:hover": {
                      color: "#F6FF00",
                    },
                  }}
                >
                  <AccessTimeIcon />
                </IconButton>
              ),
            }}
            sx={{
                bgcolor: "transparent",
                borderRadius: "100px",
                color: "#ffffff",
                "& .MuiInputLabel-root": {
                  color: "#ffffff", // Label color
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "100px",
                  fontSize: "16px",
                      fontWeight: "200",
                  border: "1px solid transparent",
                    background:
                      "linear-gradient(#0A0A0B, #0A0A0B) padding-box, radial-gradient(circle at top center, #fff -10%, #373737 40%) border-box",
                  "& fieldset": {                        
                    borderRadius: "100px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#F6FF00", // Hovered border color
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#F6FF00", // Focused border color
                  },
                  "& input": {
                    color: "#ffffff", // Text color inside input
                  },
                  "& input::-webkit-calendar-picker-indicator": {
                    display: "none",
                  },
                  "& input::-moz-calendar-picker-indicator": {
                    display: "none",
                  },
                },
              }}
          />
        </Grid>
      )}
    />
  );
};

export default CustomTimePicker;
